import { useState, type ReactNode } from "react";
import {
  Alert, AppBar, Box, Button, Chip, CircularProgress, Dialog, Divider, Link, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Toolbar, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useQuery } from "@tanstack/react-query";
import {
  billPdfUrl, fetchDuplicateCandidate, normalizeInvoice,
  type BillDraftContent, type DuplicateCandidate, type DuplicateCheckStatus,
} from "@/api/bills";

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const fmtUsd = (n: number | string | null | undefined) =>
  n == null || n === "" || Number.isNaN(Number(n)) ? "" : USD.format(Number(n));


// NetSuite SuiteQL dates come back as M/D/YYYY; drafts store YYYY-MM-DD.
export function toIsoDate(v: string | null | undefined): string {
  if (!v) return "";
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v);
  if (us) return `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
  return v.slice(0, 10);
}

const sameAmount = (a: number | string | null | undefined, b: number | string | null | undefined) =>
  a != null && b != null && a !== "" && b !== "" && Math.abs(Math.abs(Number(a)) - Math.abs(Number(b))) < 0.01;

// Highlight a value that differs from this draft's: amber background, bold.
const MATCH_LABEL: Record<DuplicateCandidate["match"], string> = {
  strong: "Strong match", overlap: "Line overlap", possible: "Possible match",
};

const mismatchSx = { bgcolor: "warning.light", fontWeight: 700, borderRadius: 0.5, px: 0.5 } as const;

function draftAmount(content: BillDraftContent): number | null {
  if (content.totals?.amount != null) return Number(content.totals.amount);
  const lines = content.lines ?? [];
  return lines.length ? lines.reduce((s, l) => s + (Number(l.amount) || 0), 0) : null;
}

function Field({ label, value, mismatch }: { label: string; value: ReactNode; mismatch?: boolean }) {
  return (
    <TableRow>
      <TableCell sx={{ color: "text.secondary", width: 130, py: 0.5 }}>{label}</TableCell>
      <TableCell sx={{ py: 0.5 }}>
        <Box component="span" sx={mismatch ? mismatchSx : undefined}>{value || "-"}</Box>
      </TableCell>
    </TableRow>
  );
}

// Full-screen side-by-side: this draft (and its invoice PDF) on the left, each
// existing bill the duplicate check found on the right, differences
// highlighted. The reviewer either cancels or confirms it is not a duplicate,
// which posts with confirm_duplicate and is recorded server-side.
export function DuplicateCompareDialog({
  open, draftId, content, vendorLabel, pdfAvailable, candidates, checkStatus, posting, error,
  onCancel, onConfirm, compareOnly,
}: {
  open: boolean;
  draftId: number;
  content: BillDraftContent;
  vendorLabel?: string | null;
  pdfAvailable: boolean;
  candidates: DuplicateCandidate[];
  checkStatus?: DuplicateCheckStatus;
  posting?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm?: () => void;
  // From the banner: just look, no post button.
  compareOnly?: boolean;
}) {
  const amount = draftAmount(content);
  const noInvoice = normalizeInvoice(content.header.invoice_number) === "";
  const strong = candidates.filter((c) => c.match === "strong").length;
  const overlap = candidates.filter((c) => c.match === "overlap").length;

  return (
    <Dialog fullScreen open={open} onClose={onCancel} aria-labelledby="duplicate-compare-title">
      <AppBar position="sticky" color={strong + overlap > 0 ? "error" : "warning"} elevation={1}>
        <Toolbar sx={{ gap: 2 }}>
          <Typography id="duplicate-compare-title" variant="h6" sx={{ flexGrow: 1 }}>
            Possible duplicate: {candidates.length} existing bill{candidates.length === 1 ? "" : "s"} look like this one
            {strong > 0 ? ` (${strong} with the same invoice number)` : ""}
            {overlap > 0 ? ` (${overlap} with lines already billed on the same project)` : ""}
          </Typography>
          <Button color="inherit" startIcon={<CloseIcon />} onClick={onCancel} disabled={posting}>
            Cancel
          </Button>
          {!compareOnly && onConfirm && (
            <Button variant="contained" color="inherit" sx={{ color: "text.primary" }} onClick={onConfirm} disabled={posting}>
              {posting ? "Posting…" : "Not a duplicate, post anyway"}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {error && <Alert severity="error" sx={{ m: 2, mb: 0 }}>{error}</Alert>}
      {!compareOnly && candidates.some((c) => c.invoice_changed) && (
        <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
          The invoice number on this bill was changed after it matched an existing bill on invoice number. Changing
          the number doesn't clear the match, so confirm it is really a different invoice before posting.
        </Alert>
      )}
      {!compareOnly && overlap > 0 && (
        <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
          Lines on this bill repeat lines on an existing bill for the same project (same item and quantity, or the
          same expense account and amount). Make sure this invoice isn't billing the same material or work again.
        </Alert>
      )}
      {!compareOnly && noInvoice && candidates.some((c) => c.match === "possible") && (
        <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
          This bill has no invoice number, so the same amount on a nearby date is the only way to spot a duplicate.
          Confirm it is not one of the bills below before posting.
        </Alert>
      )}
      {checkStatus === "check_unavailable" && (
        <Alert severity="info" sx={{ m: 2, mb: 0 }}>
          NetSuite couldn't be searched just now, so bills keyed by hand may be missing from this list.
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, p: 2, flex: 1, minHeight: 0 }}>
        {/* This draft */}
        <Paper variant="outlined" sx={{ flex: "0 0 42%", minWidth: 0, display: "flex", flexDirection: "column", overflow: "auto" }}>
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>This bill (about to post)</Typography>
            <Table size="small">
              <TableBody>
                <Field label="Vendor" value={vendorLabel ?? content.header.vendor_name} />
                <Field label="Invoice #" value={content.header.invoice_number} />
                <Field label="Date" value={content.header.trandate} />
                <Field label="Amount" value={fmtUsd(amount)} />
                <Field label="Memo" value={content.header.memo} />
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Lines ({(content.lines ?? []).length})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Description</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Amount</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {(content.lines ?? []).map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>{l.description}</TableCell>
                    <TableCell align="right">{l.quantity ?? ""}</TableCell>
                    <TableCell align="right">{fmtUsd(l.amount)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtUsd(amount)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
          {pdfAvailable && (
            <Box sx={{ flex: 1, minHeight: 360, borderTop: 1, borderColor: "divider" }}>
              <object data={billPdfUrl(draftId)} type="application/pdf" width="100%" height="100%" aria-label="Invoice PDF">
                <Typography sx={{ p: 2 }}>PDF preview unavailable. <a href={billPdfUrl(draftId)}>Download</a></Typography>
              </object>
            </Box>
          )}
        </Paper>

        {/* Existing bills */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: "auto" }}>
          <Stack spacing={2}>
            {candidates.map((c) => (
              <CandidateCard
                key={`${c.source}-${c.netsuite_id ?? c.draft_id}`}
                draftId={draftId} candidate={c} content={content} draftTotal={amount}
              />
            ))}
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
}

function CandidateCard({ draftId, candidate: c, content, draftTotal }: {
  draftId: number; candidate: DuplicateCandidate; content: BillDraftContent; draftTotal: number | null;
}) {
  const [showLines, setShowLines] = useState(false);
  const lines = useQuery({
    queryKey: ["duplicate_candidate", draftId, c.netsuite_id],
    queryFn: () => fetchDuplicateCandidate(draftId, c.netsuite_id as number),
    enabled: showLines && c.netsuite_id != null,
    staleTime: 60_000,
  });

  const invoiceDiffers = normalizeInvoice(c.tranid) !== normalizeInvoice(content.header.invoice_number);
  const dateDiffers = toIsoDate(c.trandate) !== toIsoDate(content.header.trandate);
  const amountDiffers = !sameAmount(c.amount, draftTotal);
  const draftLineAmounts = (content.lines ?? []).map((l) => l.amount);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderColor: c.match === "possible" ? "warning.main" : "error.main" }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap" }}>
        <Chip size="small" color={c.match === "possible" ? "warning" : "error"}
          label={MATCH_LABEL[c.match]} />
        <Typography variant="body2" color="text.secondary">{c.reasons.join(", ")}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Chip size="small" variant="outlined"
          label={c.entered_via === "ap_triage" ? "Entered via FP Accounting" : "Entered manually in NetSuite"} />
        {c.netsuite_url && (
          <Button size="small" endIcon={<OpenInNewIcon />} component="a" href={c.netsuite_url}
            target="_blank" rel="noopener noreferrer">
            Open in NetSuite
          </Button>
        )}
        {c.draft_id != null && (
          <Link href={`/bills/${c.draft_id}`} target="_blank" rel="noopener noreferrer" variant="body2">
            Draft #{c.draft_id}
          </Link>
        )}
      </Stack>
      <Table size="small">
        <TableBody>
          <Field label="Record" value={`${c.record_type === "bill_credit" ? "Bill Credit" : "Vendor Bill"}${c.netsuite_id ? ` #${c.netsuite_id}` : ""}`} />
          <Field label="Invoice #" value={c.tranid} mismatch={invoiceDiffers} />
          <Field label="Date" value={toIsoDate(c.trandate)} mismatch={dateDiffers} />
          <Field label="Amount" value={fmtUsd(c.amount)} mismatch={amountDiffers} />
          <Field label="Status" value={c.status ?? c.draft_status} />
          <Field label="Memo" value={c.memo} />
          <Field label="Entered by" value={c.entered_by} />
          <Field label="Entered" value={c.created_at ? toIsoDate(c.created_at) : ""} />
        </TableBody>
      </Table>

      <Divider sx={{ my: 1 }} />
      {c.netsuite_id == null ? (
        <Typography variant="caption" color="text.secondary">
          Not in NetSuite yet (still submitting). Open the draft to see its lines.
        </Typography>
      ) : !showLines ? (
        <Button size="small" onClick={() => setShowLines(true)}>Load lines from NetSuite</Button>
      ) : lines.isLoading ? (
        <CircularProgress size={20} />
      ) : lines.isError ? (
        <Alert severity="warning">Couldn't load the lines from NetSuite.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item / account</TableCell><TableCell>Description</TableCell>
              <TableCell align="right">Qty</TableCell><TableCell align="right">Amount</TableCell>
              <TableCell>Class</TableCell><TableCell>Location</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(lines.data?.lines ?? []).map((l, i) => {
              const unmatched = !draftLineAmounts.some((a) => sameAmount(a, l.amount));
              return (
                <TableRow key={l.line ?? i}>
                  <TableCell>{l.item_name ?? l.account_name}</TableCell>
                  <TableCell>{l.description}</TableCell>
                  <TableCell align="right">{l.quantity ?? ""}</TableCell>
                  <TableCell align="right">
                    <Box component="span" sx={unmatched ? mismatchSx : undefined}>{fmtUsd(l.amount)}</Box>
                  </TableCell>
                  <TableCell>{l.class_name}</TableCell>
                  <TableCell>{l.location}</TableCell>
                </TableRow>
              );
            })}
            {(lines.data?.lines ?? []).length !== (content.lines ?? []).length && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box component="span" sx={mismatchSx}>
                    {(lines.data?.lines ?? []).length} line(s) here vs {(content.lines ?? []).length} on this bill
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
